import { Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ExpenseProvider } from '@/contexts/expense-context'
import { useAuth } from '@/hooks/useAuth'
import AuthPage from '@/pages/auth/page'
import DashboardPage from '@/pages/dashboard/page'
import InsightsPage from '@/pages/insights/page'
import ExpensesPage from '@/pages/expenses/page'
import AddExpensePage from '@/pages/add/page'
import SettingsPage from '@/pages/settings/page'
import CategoriesPage from '@/pages/settings/categories/page'
import RecurringExpensesPage from '@/pages/settings/recurring/page'
import { BottomNav } from '@/components/bottom-nav'
import { PageSkeleton } from '@/components/skeleton'

// Create a client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
            retry: 1,
            refetchOnWindowFocus: false,
        },
    },
})

function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { user, loading } = useAuth()

    if (loading) {
        return <PageSkeleton />
    }

    if (!user) {
        return <Navigate to="/auth" replace />
    }

    return <>{children}</>
}

function App() {
    const { user, loading } = useAuth()

    if (loading) {
        return <PageSkeleton />
    }

    return (
        <QueryClientProvider client={queryClient}>
            <ExpenseProvider>
                <div className="min-h-screen bg-background">
                    <Routes>
                        <Route
                            path="/auth"
                            element={user ? <Navigate to="/dashboard" replace /> : <AuthPage />}
                        />

                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute>
                                    <DashboardPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/insights"
                            element={
                                <ProtectedRoute>
                                    <InsightsPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/expenses"
                            element={
                                <ProtectedRoute>
                                    <ExpensesPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/add"
                            element={
                                <ProtectedRoute>
                                    <AddExpensePage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/settings"
                            element={
                                <ProtectedRoute>
                                    <SettingsPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/settings/categories"
                            element={
                                <ProtectedRoute>
                                    <CategoriesPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/settings/recurring"
                            element={
                                <ProtectedRoute>
                                    <RecurringExpensesPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>

                    {user && <BottomNav />}
                </div>
            </ExpenseProvider>
        </QueryClientProvider>
    )
}

export default App
